import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { fmtDate } from "@/components/sci/useSciPreview";
import { resetGuidesCache } from "@/lib/subjectGuides";

const failed = (e) => toast.error(e?.response?.data?.detail ? String(e.response.data.detail) : "The request failed; nothing was changed.");

function Issues({ res }) {
  if (!res) return null;
  const rows = [...(res.errors || []).map((e) => ["error", e]), ...(res.warnings || []).map((e) => ["warning", e])];
  return (
    <div className="mt-3 text-sm" data-testid="guide-admin-result">
      <p className="font-semibold">Result: {res.result}{res.version ? ` · version ${res.version}` : ""}</p>
      {res.conflict && <p className="text-amber-800">{res.conflict.reason}{res.conflict.changed_paths ? ` (changed: ${res.conflict.changed_paths.join(", ")})` : ""}</p>}
      <ul className="mt-1 max-h-48 overflow-auto">{rows.map(([k, e], i) => <li key={i} className={k === "error" ? "text-red-700" : "text-slate-600"}>{k}: {e.path} — {e.message}</li>)}</ul>
    </div>
  );
}

function History({ versions, liveVersion, onRollback }) {
  return (
    <ol className="mt-3 space-y-1 text-sm" data-testid="guide-admin-history">
      {versions.map((v) => (
        <li key={v.version} className="flex flex-wrap items-center gap-2">
          <span className="font-mono">v{v.version}</span><span>{v.origin}</span><span className="text-slate-500">{v.actor} · {fmtDate(v.created_at)}</span>
          {v.version === liveVersion ? <span className="rounded bg-emerald-100 px-2 text-emerald-800">live</span>
            : <button type="button" className="underline" onClick={() => onRollback(v.version)} data-testid={`guide-admin-rollback-${v.version}`}>Restore this version</button>}
        </li>
      ))}
    </ol>
  );
}

// Admin-only live editing on the published guide: same validator, fingerprint guard and history as ChatGPT batches.
export function GuideAdmin({ slug, onPublished }) {
  const [state, setState] = useState(null);
  const [text, setText] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const navigate = useNavigate();
  const load = useCallback(() => api.get(`/admin/subject-guides/${slug}`).then((r) => {
    setState(r.data);
    setText(JSON.stringify(r.data.content, null, 2));
  }), [slug]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const fp = state?.guide?.live_fingerprint ?? null;
  const send = async (dry) => {
    let content;
    try { content = JSON.parse(text); } catch { return setRes({ result: "failed", errors: [{ path: "", message: "Not valid JSON" }] }); }
    const r = await api.put(`/admin/subject-guides/${slug}`, { content, expected_fingerprint: fp, dry_run: dry }).catch(failed);
    if (!r) return;
    setRes(r.data);
    if (r.data.result === "published") { toast.success(`Published version ${r.data.version}`); resetGuidesCache(); load(); onPublished(); }
  };
  const rollback = async (v) => {
    const r = await api.post(`/admin/subject-guides/${slug}/rollback`, { to_version: v, expected_fingerprint: fp }).catch(failed);
    if (!r) return;
    setRes(r.data);
    if (r.data.result === "published") { toast.success(`Restored as version ${r.data.version}`); resetGuidesCache(); load(); onPublished(); }
  };
  const withdraw = async () => {
    if (!confirming) return setConfirming(true);
    setConfirming(false);
    const r = await api.post(`/admin/subject-guides/${slug}/withdraw`, { expected_fingerprint: fp }).catch(failed);
    if (!r) return;
    setRes(r.data);
    if (r.data.result === "withdrawn") {
      toast.success("Guide withdrawn; it is hidden from students. Restore it from the admin box on Subjects.");
      resetGuidesCache();
      navigate("/subjects");
    }
  };
  return (
    <section className="mx-auto mt-6 max-w-5xl rounded-lg border border-slate-300 bg-white p-4 text-slate-800" data-testid="guide-admin">
      <button type="button" className="font-semibold underline" onClick={() => setOpen(!open)} data-testid="guide-admin-toggle">
        {open ? "Close guide editor" : "Edit this guide (admins only)"}
      </button>
      {open && state && (
        <div className="mt-3">
          <p className="text-sm text-slate-600">Live version {state.guide?.live_version} · fingerprint <span className="font-mono">{fp?.slice(0, 19)}…</span>. Saving runs the same checks as ChatGPT uploads and creates a new version.</p>
          <textarea className="mt-2 h-96 w-full rounded border p-2 font-mono text-xs" value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} data-testid="guide-admin-json" />
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" className="rounded border px-3 py-1" onClick={() => send(true)} data-testid="guide-admin-check">Check</button>
            <button type="button" className="rounded bg-[#175d51] px-3 py-1 text-white" onClick={() => send(false)} data-testid="guide-admin-save">Save and publish</button>
            <button type="button" className="rounded border border-red-300 px-3 py-1 text-red-700" onClick={withdraw} data-testid="guide-admin-withdraw">{confirming ? "Click again to withdraw" : "Withdraw guide"}</button>
          </div>
          <Issues res={res} />
          <History versions={state.versions} liveVersion={state.guide?.live_version} onRollback={rollback} />
        </div>
      )}
    </section>
  );
}
